// API service for communicating with the backend
const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : 'https://api.shop.bg502.ru/api/v1'

// Debug log to see what URL is being used
console.log('API_BASE_URL:', API_BASE_URL)
console.log('VITE_API_URL:', import.meta.env.VITE_API_URL)

// Types for API responses
export interface Product {
  id: string
  name: string
  description?: string
  short_description?: string
  price: number
  compare_price?: number
  currency?: string
  quantity?: number
  sku?: string
  status: string
  is_featured: boolean
  condition?: string  // new, like_new, used, refurbished, damaged, for_parts
  tags?: string[]  // Tag IDs array (legacy field)
  tag_list?: Tag[]  // Full tag objects from backend
  category_id?: string
  category?: {
    id: string
    name: string
    description?: string
    slug: string
  }
  attributes?: Record<string, any>  // Category-specific spec values (e.g. {ram_gb:16})
  image_url?: string  // Single image per product
  images?: ProductImage[]  // Keep for backward compatibility
  videos?: ProductVideo[]  // Product videos
  country_code?: string | null  // Product location
  city_name?: string | null     // Product city
  location_lat?: number | null       // Precise product point latitude
  location_lng?: number | null       // Precise product point longitude
  location_radius_km?: number        // Radius (km) around the point
  payment_methods?: string[]  // cash, card, bank_transfer, crypto, paypal
  is_reserved?: boolean
  reserved_by?: string
  reserved_at?: string
  reservation_expires_at?: string
  owner?: {
    telegram_id: number
    username?: string
    first_name?: string
  }
  created_at: string
  updated_at: string
}

export interface ProductImage {
  id: string
  product_id: string
  image_url: string
  alt_text?: string
  sort_order: number
  is_primary: boolean
  created_at: string
}

export interface ProductVideo {
  id: string
  product_id: string
  video_url: string
  thumbnail_url?: string
  duration_seconds?: number
  sort_order: number
  created_at: string
}

export interface Category {
  id: string
  name: string
  description?: string
  slug: string
  image_url?: string
  sort_order: number
  is_active: boolean
  product_count?: number
  created_at: string
  updated_at: string
}

/** Recursive category tree node with attributes and children */
export interface CategoryNode {
  id: string
  slug: string
  icon?: string
  name: string
  attributes?: Array<{
    key: string
    type: 'int' | 'decimal' | 'enum' | 'bool' | 'text'
    label: string | { en: string; ru?: string }
    unit?: string
    range?: boolean
    options?: Array<{ v: string; en?: string; ru?: string; label?: string }>
    display_group?: string
  }>
  is_custom: boolean
  product_count: number
  children?: CategoryNode[]
}

export interface Cart {
  id: string
  items: CartItem[]
  subtotal: number
  total: number
  count: number
  created_at: string
  updated_at: string
}

export interface CartItem {
  id: string
  cart_id: string
  product_id: string
  variant_id?: string
  quantity: number
  price: number
  product?: Product
  variant?: any
  created_at: string
  updated_at: string
}

export interface Order {
  id: string
  user_id: string
  order_number: string
  status: string
  subtotal: number
  tax_amount: number
  shipping_amount: number
  discount_amount: number
  total_amount: number
  currency: string
  items?: OrderItem[]
  created_at: string
  updated_at: string
}

export interface OrderItem {
  id: string
  order_id: string
  product_id: string
  variant_id?: string
  quantity: number
  price: number
  total: number
  product?: Product
}

// Review types
export interface Review {
  id: string
  reviewer_id: string
  reviewee_id: string
  order_id?: string
  review_type: 'seller' | 'buyer'
  rating: number
  comment?: string
  created_at: string
  updated_at: string
  reviewer?: {
    id: string
    telegram_id: number
    username?: string
    first_name?: string
    avatar_url?: string
  }
}

export interface ReviewListResponse {
  reviews: Review[]
  total: number
  page: number
  limit: number
  total_pages: number
  avg_rating: number
}

export interface UserRatingResponse {
  avg_seller_rating: number
  seller_review_count: number
  avg_buyer_rating: number
  buyer_review_count: number
}

export interface Tag {
  id: string
  slug: string
  name_key: string
  icon?: string
  sort_order: number
  is_active: boolean
  product_count?: number
  created_at: string
  updated_at: string
}

export interface TagsResponse {
  tags: Tag[]
  count: number
}

export interface ProductListResponse {
  products: Product[]
  total: number
  page: number
  limit: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface OrderListResponse {
  orders: Order[]
  total: number
  page: number
  limit: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface ApiError {
  error: string
  message: string
  details?: string
}

// User location types
export interface UserLocation {
  home_country_code: string | null
  home_city_name: string | null
  location_lat?: number | null
  location_lng?: number | null
  location_radius_km?: number | null
}

export interface UserLocationRequest {
  home_country_code?: string | null
  home_city_name?: string | null
  location_lat?: number | null
  location_lng?: number | null
  location_radius_km?: number | null
}

export interface FavoriteLocation {
  id: string
  country_code: string
  city_name: string
  is_primary: boolean
  label?: string | null
  // Optional approximate map point attached to this saved place
  location_lat?: number | null
  location_lng?: number | null
  location_radius_km?: number | null
}

// Helper function to decode JWT token without verification (just to read the payload)
function decodeJWT(token: string): any {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch (e) {
    console.error('Failed to decode JWT:', e)
    return null
  }
}

// Helper function to get auth token
export function getAuthToken(): string | null {
  const tg = (window as any).Telegram?.WebApp
  const currentTelegramUserId = tg?.initDataUnsafe?.user?.id

  console.log('[getAuthToken] Starting token validation', {
    hasWebApp: !!tg,
    currentTelegramUserId,
    hasInitData: !!tg?.initData
  })

  // Check if we have a valid JWT token in localStorage
  const token = localStorage.getItem('auth_token')
  console.log('[getAuthToken] Cached token status:', {
    hasToken: !!token,
    tokenLength: token?.length,
    tokenPrefix: token?.substring(0, 20)
  })

  if (token && token.length > 20 && !token.startsWith('telegram:')) {
    // Reject tokens that are expired (or about to expire) so callers run the
    // refresh/re-auth flow instead of sending a doomed request. This applies in
    // every branch, including the no-Telegram-user fallback below.
    const expCheck = decodeJWT(token)
    if (expCheck?.exp && expCheck.exp * 1000 < Date.now() + 30_000) {
      console.warn('[getAuthToken] ⚠️ Cached token expired, needs refresh')
      localStorage.removeItem('auth_token')
      return null
    }

    // Decode the JWT to check if it belongs to the current Telegram user
    if (currentTelegramUserId) {
      const decoded = decodeJWT(token)
      console.log('[getAuthToken] Decoded JWT payload:', {
        telegram_id: decoded?.telegram_id,
        user_id: decoded?.user_id,
        exp: decoded?.exp
      })

      if (decoded && decoded.telegram_id) {
        if (decoded.telegram_id === currentTelegramUserId) {
          console.log('[getAuthToken] ✅ Token matches current user - using cached token')
          return token
        } else {
          console.error('[getAuthToken] ❌ TOKEN MISMATCH DETECTED!', {
            cached_telegram_id: decoded.telegram_id,
            current_telegram_id: currentTelegramUserId,
            type_cached: typeof decoded.telegram_id,
            type_current: typeof currentTelegramUserId
          })
          localStorage.removeItem('auth_token')
          return null
        }
      } else {
        console.warn('[getAuthToken] ⚠️ Could not decode JWT or missing telegram_id, clearing token')
        localStorage.removeItem('auth_token')
        return null
      }
    } else {
      // No Telegram user info available, use cached token as fallback
      console.warn('[getAuthToken] ⚠️ No Telegram user ID available, using cached token as fallback')
      return token
    }
  }

  // For Telegram Mini Apps, we need to authenticate with the backend first
  if (tg?.initData) {
    console.log('[getAuthToken] No cached token, need to authenticate with backend')
    // We'll handle the authentication in a separate function
    // For now, return null so the request fails and we can handle it
    return null
  }

  console.log('[getAuthToken] No auth token available and no Telegram initData')
  return null
}

// Helper function to authenticate with Telegram
async function authenticateWithTelegram(): Promise<string | null> {
  const tg = (window as any).Telegram?.WebApp
  if (!tg?.initData) {
    console.log('[authenticateWithTelegram] No Telegram initData available')
    return null
  }

  try {
    const currentUser = tg.initDataUnsafe?.user
    console.log('[authenticateWithTelegram] Starting authentication', {
      telegram_user_id: currentUser?.id,
      username: currentUser?.username,
      first_name: currentUser?.first_name,
      initData_length: tg.initData?.length
    })

    const response = await fetch(`${API_BASE_URL}/auth/telegram`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        init_data: tg.initData
      })
    })

    console.log('[authenticateWithTelegram] Response status:', response.status)

    if (!response.ok) {
      console.error('[authenticateWithTelegram] ❌ Authentication failed:', response.status, response.statusText)
      // Try to get error details
      try {
        const errorData = await response.json()
        console.error('[authenticateWithTelegram] Error details:', errorData)
      } catch (e) {
        console.error('[authenticateWithTelegram] Could not parse error response')
      }
      return null
    }

    const data = await response.json()
    console.log('[authenticateWithTelegram] ✅ Response received:', {
      has_access_token: !!data.access_token,
      token_length: data.access_token?.length
    })

    if (data.access_token) {
      // Decode the new token to verify it's for the correct user
      const decoded = decodeJWT(data.access_token)
      console.log('[authenticateWithTelegram] New JWT token decoded:', {
        telegram_id: decoded?.telegram_id,
        user_id: decoded?.user_id,
        expected_telegram_id: currentUser?.id
      })

      // Store the JWT + refresh tokens
      localStorage.setItem('auth_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }
      console.log('[authenticateWithTelegram] ✅ Tokens stored in localStorage')

      // Cache the server-provided avatar (re-hosted from Telegram) so the UI can
      // show a real photo even when initData carries no photo_url. Notify listeners
      // so avatar components can update reactively within the same session.
      const avatarUrl = data.user?.avatar_url
      if (avatarUrl) {
        localStorage.setItem('auth_avatar_url', avatarUrl)
      } else {
        localStorage.removeItem('auth_avatar_url')
      }
      window.dispatchEvent(new CustomEvent('auth:avatar', { detail: avatarUrl || null }))

      return data.access_token
    } else {
      console.error('[authenticateWithTelegram] ❌ No access_token in response:', data)
      return null
    }
  } catch (error) {
    console.error('[authenticateWithTelegram] ❌ Network or parsing error:', error)
    return null
  }
}

// Exchange the stored refresh token for a fresh access token. Returns the new
// access token, or null if there is no refresh token or it was rejected.
async function refreshAuthToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    console.log('[refreshAuthToken] No refresh token stored')
    return null
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    })

    if (!response.ok) {
      console.warn('[refreshAuthToken] Refresh rejected:', response.status)
      // A rejected refresh token is dead — drop it so we fall back to initData auth.
      localStorage.removeItem('refresh_token')
      return null
    }

    const data = await response.json()
    if (data.access_token) {
      localStorage.setItem('auth_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }
      console.log('[refreshAuthToken] ✅ Token refreshed')
      return data.access_token
    }
    return null
  } catch (error) {
    console.error('[refreshAuthToken] Network error:', error)
    return null
  }
}

// Single-flight token acquisition: refresh first (survives stale/absent Telegram
// initData), then full initData auth. Concurrent callers share one attempt so a
// page mounting five requests doesn't stampede the auth endpoints.
let authInFlight: Promise<string | null> | null = null
function ensureAuthToken(): Promise<string | null> {
  if (!authInFlight) {
    authInFlight = (async () => {
      const refreshed = await refreshAuthToken()
      if (refreshed) return refreshed
      return authenticateWithTelegram()
    })().finally(() => {
      authInFlight = null
    })
  }
  return authInFlight
}

// Public helper for components that build their own fetch (uploads etc.):
// returns a currently-valid access token, refreshing/re-authing if needed.
export async function getValidAuthToken(): Promise<string | null> {
  const token = getAuthToken()
  if (token) return token
  return ensureAuthToken()
}

// Helper function to make authenticated requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  console.log('apiRequest: Starting request to:', url)

  let token = getAuthToken()
  console.log('apiRequest: Initial token:', token ? 'present' : 'missing')

  const headers = new Headers({
    'Content-Type': 'application/json',
  })

  // Add any existing headers
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers.set(key, value)
      })
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers.set(key, value)
      })
    } else {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers.set(key, value)
        }
      })
    }
  }

  // If no valid cached token, acquire one (refresh-token first, then initData auth)
  if (!token) {
    console.log('No valid auth token, acquiring one...')
    token = await ensureAuthToken()
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // If authentication failed, try refresh/re-auth once and retry
  if (response.status === 401) {
    console.log('Authentication failed, attempting to re-authenticate...')
    localStorage.removeItem('auth_token')
    const newToken = await ensureAuthToken()
    if (newToken) {
      // Retry the request with new token
      headers.set('Authorization', `Bearer ${newToken}`)
      const retryResponse = await fetch(url, {
        ...options,
        headers,
      })

      if (retryResponse.ok) {
        return retryResponse.json()
      }

      // If retry also failed, continue with error handling below
      console.error('Retry request also failed')
    }
  }

  if (!response.ok) {
    console.error(`API request failed: ${response.status} ${response.statusText}`)
    console.error(`URL: ${url}`)

    const errorData: ApiError = await response.json().catch(() => ({
      error: 'unknown_error',
      message: `Request failed: ${response.status} ${response.statusText}`,
    }))

    console.error('Error response:', errorData)
    throw new Error(errorData.message || 'Request failed')
  }

  return response.json()
}

// Product API
export const productApi = {
  // Get products with pagination and filters
  getProducts: async (params: {
    page?: number
    limit?: number
    category_id?: string
    status?: string
    owner_id?: string
    sort_by?: string
    sort_order?: string
  } = {}): Promise<ProductListResponse> => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })
    const query = searchParams.toString()
    return apiRequest(`/products${query ? `?${query}` : ''}`)
  },

  // Get single product
  getProduct: async (id: string): Promise<Product> => {
    return apiRequest(`/products/${id}`)
  },

  // Get the authenticated user's favorite products. Goes through apiRequest so the
  // Telegram auth/refresh flow runs (a raw fetch here would 401 on cold start).
  getFavorites: async (): Promise<Product[]> => {
    const data = await apiRequest<{ favorites?: Product[] }>(`/favorites`)
    return data.favorites || []
  },

  // Search products
  searchProducts: async (params: {
    query?: string
    category_id?: string
    category_ids?: string
    min_price?: number
    max_price?: number
    tags?: string
    attrs?: Record<string, any>  // category-attribute filters: key -> value | {min,max}
    page?: number
    limit?: number
    sort_by?: string
    sort_order?: string
  } = {}): Promise<ProductListResponse> => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return
      if (key === 'attrs') {
        // Drop empty entries, then JSON-encode for the backend to parse.
        const cleaned = Object.fromEntries(
          Object.entries(value as Record<string, any>).filter(([, v]) => {
            if (v === undefined || v === null || v === '') return false
            if (typeof v === 'object') return v.min || v.max
            return true
          })
        )
        if (Object.keys(cleaned).length > 0) searchParams.append('attrs', JSON.stringify(cleaned))
      } else {
        searchParams.append(key, value.toString())
      }
    })
    return apiRequest(`/products/search?${searchParams.toString()}`)
  },

  // Get category tree (hierarchical with attributes)
  getCategoryTree: async (lang: string = 'en'): Promise<{ categories: import('./api').CategoryNode[] }> => {
    return apiRequest(`/products/categories/tree?lang=${lang}`)
  },

  // Suggest a category that isn't in the tree (operator adds it in code)
  submitCategorySuggestion: async (data: {
    proposed_name: string
    parent_category_id?: string
    note?: string
  }): Promise<{ id: string; status: string }> => {
    return apiRequest('/products/category-suggestions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Get flat categories (legacy)
  getCategories: async (): Promise<{ categories: Category[]; count: number }> => {
    return apiRequest('/products/categories')
  },

  // Get all tags
  getTags: async (): Promise<TagsResponse> => {
    return apiRequest('/products/tags')
  },

  // Admin: Create product
  createProduct: async (data: {
    name: string
    description?: string
    price: number
    currency: string
    quantity: number
    status?: string
    tag_ids?: string[]
    location_lat?: number | null
    location_lng?: number | null
    location_radius_km?: number
  }): Promise<Product> => {
    return apiRequest('/admin/products', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Admin: Get product for editing (includes tags)
  getProductForEdit: async (id: string): Promise<Product> => {
    return apiRequest(`/admin/products/${id}`)
  },

  // Admin: Update product
  updateProduct: async (id: string, data: {
    name?: string
    description?: string | null
    price?: number
    currency?: string
    quantity?: number
    status?: string
    tag_ids?: string[]
    category_id?: string | null
    attributes?: Record<string, any>
    country_code?: string | null
    city_name?: string | null
    location_lat?: number | null
    location_lng?: number | null
    location_radius_km?: number
  }): Promise<Product> => {
    return apiRequest(`/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Get my products (authenticated user's products)
  getMyProducts: async (params: { active?: boolean } = {}): Promise<ProductListResponse> => {
    const searchParams = new URLSearchParams()
    if (params.active !== undefined) {
      searchParams.append('active', params.active.toString())
    }
    const query = searchParams.toString()
    return apiRequest(`/my-products${query ? `?${query}` : ''}`)
  },

  // Get personal store (public, no auth)
  getPersonalStore: async (userId: string): Promise<{
    user: {
      telegram_id: number
      username?: string
      first_name?: string
      last_name?: string
      // Approximate seller area — present only when the seller set a map point
      location_lat?: number | null
      location_lng?: number | null
      location_radius_km?: number | null
      home_city_name?: string | null
      home_country_code?: string | null
    }
    products: Product[]
    total: number
  }> => {
    return apiRequest(`/store/${userId}`)
  },

  // Send a question/message to a user (seller). The recipient gets a Telegram
  // notification with the message and the sender's username.
  contactUser: async (userId: string | number, message: string): Promise<{ message: string }> => {
    return apiRequest(`/users/${userId}/contact`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    })
  },

  // Get my store link (authenticated)
  getMyStoreLink: async (): Promise<{
    store_url: string
    telegram_share_url: string
    user_id: number
  }> => {
    return apiRequest('/my-store-link')
  },

  // Send product interest notification (authenticated)
  sendProductInterest: async (productId: string, message?: string, proposedPrice?: number): Promise<{ message: string }> => {
    return apiRequest(`/products/${productId}/interested`, {
      method: 'POST',
      body: JSON.stringify({ message, proposed_price: proposedPrice }),
    })
  },

  // Send product interest notification (public - no auth required)
  sendPublicProductInterest: async (
    productId: string,
    data: {
      message?: string
      visitor_name?: string
      telegram_id?: number
      telegram_user?: string
      proposed_price?: number
    } = {}
  ): Promise<{ message: string }> => {
    // Make request without authentication
    const url = `${API_BASE_URL}/products/${productId}/public-interest`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        error: 'unknown_error',
        message: `Request failed: ${response.status} ${response.statusText}`,
      }))
      throw new Error(errorData.message || 'Failed to send interest')
    }

    return response.json()
  },

  // Admin: Activate product
  activateProduct: async (id: string): Promise<Product> => {
    return apiRequest(`/admin/products/${id}/activate`, {
      method: 'PATCH',
    })
  },

  // Admin: Deactivate product
  deactivateProduct: async (id: string): Promise<Product> => {
    return apiRequest(`/admin/products/${id}/deactivate`, {
      method: 'PATCH',
    })
  },

  // Admin: Archive product (only for inactive products)
  archiveProduct: async (id: string): Promise<Product> => {
    return apiRequest(`/admin/products/${id}/archive`, {
      method: 'PATCH',
    })
  },

  // Update product quantity
  updateProductQuantity: async (id: string, quantity: number): Promise<Product> => {
    return apiRequest(`/products/${id}/quantity`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    })
  },

  // Admin: Delete product (only for archived products)
  deleteProduct: async (id: string): Promise<{ message: string; product_id: string }> => {
    return apiRequest(`/admin/products/${id}`, {
      method: 'DELETE',
    })
  },

  // Admin: Get presigned upload URL for direct MinIO upload
  getPresignedUploadURL: async (productId: string, data: {
    filename: string
    content_type: string
    is_primary?: boolean
  }): Promise<{
    upload_url: string
    image_id: string
    object_name: string
    public_url: string
    expires_in: number
  }> => {
    return apiRequest(`/admin/products/${productId}/images/presigned`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Admin: Confirm successful image upload
  confirmImageUpload: async (productId: string, data: {
    image_id: string
    object_name: string
    public_url: string
    alt_text?: string
    is_primary?: boolean
  }): Promise<ProductImage> => {
    return apiRequest(`/admin/products/${productId}/images/confirm`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Admin: Upload product image (legacy - through backend)
  uploadProductImage: async (productId: string, file: File, data?: {
    alt_text?: string
    is_primary?: boolean
  }): Promise<ProductImage> => {
    const formData = new FormData()
    formData.append('image', file)
    if (data?.alt_text) {
      formData.append('alt_text', data.alt_text)
    }
    if (data?.is_primary !== undefined) {
      formData.append('is_primary', data.is_primary ? 'true' : 'false')
    }

    // Get token for authentication (refresh-token first, then initData auth)
    let token = getAuthToken()
    if (!token) {
      token = await ensureAuthToken()
    }

    const headers = new Headers()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    // Don't set Content-Type - browser will set it with boundary for multipart

    const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images`, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        error: 'unknown_error',
        message: `Upload failed: ${response.status} ${response.statusText}`,
      }))
      throw new Error(errorData.message || 'Upload failed')
    }

    return response.json()
  },

  // Admin: Delete product image
  deleteProductImage: async (productId: string, imageId: string): Promise<{ message: string }> => {
    return apiRequest(`/admin/products/${productId}/images/${imageId}`, {
      method: 'DELETE',
    })
  },

  // Admin: Set primary product image
  setPrimaryProductImage: async (productId: string, imageId: string): Promise<ProductImage> => {
    return apiRequest(`/admin/products/${productId}/images/${imageId}/primary`, {
      method: 'PATCH',
    })
  },

  // User location methods
  getUserLocation: async (): Promise<UserLocation> => {
    return apiRequest('/user/location')
  },

  updateUserLocation: async (location: UserLocationRequest): Promise<void> => {
    return apiRequest('/user/location', {
      method: 'PUT',
      body: JSON.stringify(location)
    })
  },

  // Favorite locations
  getFavoriteLocations: async (): Promise<{
    favorite_locations: FavoriteLocation[]
    count: number
  }> => {
    return apiRequest('/user/favorite-locations')
  },

  addFavoriteLocation: async (location: {
    country_code: string
    city_name: string
    set_as_home?: boolean
    location_lat?: number | null
    location_lng?: number | null
    location_radius_km?: number | null
  }): Promise<{
    message: string
    location: FavoriteLocation
  }> => {
    return apiRequest('/user/favorite-locations', {
      method: 'POST',
      body: JSON.stringify(location)
    })
  },

  // Update a saved place: attach/change/clear its map point or label.
  // Send location_lat: 0 AND location_lng: 0 to clear the point (backend sentinel).
  updateFavoriteLocation: async (locationId: string, data: {
    label?: string | null
    location_lat?: number | null
    location_lng?: number | null
    location_radius_km?: number | null
  }): Promise<{
    message: string
    location: FavoriteLocation
  }> => {
    return apiRequest(`/user/favorite-locations/${locationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  deleteFavoriteLocation: async (locationId: string): Promise<void> => {
    return apiRequest(`/user/favorite-locations/${locationId}`, {
      method: 'DELETE'
    })
  },

  setPrimaryLocation: async (locationId: string): Promise<{
    message: string
    home_country_code: string
    home_city_name: string
  }> => {
    return apiRequest(`/user/favorite-locations/${locationId}/primary`, {
      method: 'PUT'
    })
  },
}

// Review API
export const reviewApi = {
  createReview: async (data: {
    reviewee_id: string
    order_id?: string
    review_type: 'seller' | 'buyer'
    rating: number
    comment?: string
  }): Promise<Review> => {
    return apiRequest('/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  getUserReviews: async (userId: string, params?: {
    type?: 'seller' | 'buyer'
    page?: number
    limit?: number
  }): Promise<ReviewListResponse> => {
    const searchParams = new URLSearchParams()
    if (params?.type) searchParams.append('type', params.type)
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    const query = searchParams.toString()
    return apiRequest(`/users/${userId}/reviews${query ? `?${query}` : ''}`)
  },

  getUserRatings: async (userId: string): Promise<UserRatingResponse> => {
    return apiRequest(`/users/${userId}/ratings`)
  },
}

// Cart API
export const cartApi = {
  // Get user's cart
  getCart: async (): Promise<Cart> => {
    return apiRequest('/cart')
  },

  // Add item to cart
  addToCart: async (data: {
    product_id: string
    variant_id?: string
    quantity: number
  }): Promise<{ message: string; cart_item: CartItem }> => {
    return apiRequest('/cart/items', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update cart item
  updateCartItem: async (
    itemId: string,
    data: { quantity: number }
  ): Promise<{ message: string; cart_item: CartItem }> => {
    return apiRequest(`/cart/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Remove item from cart
  removeCartItem: async (itemId: string): Promise<{ message: string }> => {
    return apiRequest(`/cart/items/${itemId}`, {
      method: 'DELETE',
    })
  },

  // Clear cart
  clearCart: async (): Promise<{ message: string }> => {
    return apiRequest('/cart', {
      method: 'DELETE',
    })
  },
}

// Order API
export const orderApi = {
  // Create order
  createOrder: async (data: {
    shipping_address?: any
    billing_address?: any
    notes?: string
    metadata?: any
  }): Promise<{ order: Order; message: string }> => {
    return apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Get user orders
  getOrders: async (params: {
    page?: number
    limit?: number
    status?: string
  } = {}): Promise<OrderListResponse> => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })
    const query = searchParams.toString()
    return apiRequest(`/orders${query ? `?${query}` : ''}`)
  },

  // Get single order
  getOrder: async (id: string): Promise<Order> => {
    return apiRequest(`/orders/${id}`)
  },

  // Cancel order
  cancelOrder: async (id: string): Promise<{ message: string; order_id: string; order_number: string }> => {
    return apiRequest(`/orders/${id}/cancel`, {
      method: 'POST',
    })
  },
}

// Auth API (for admin detection)
export const authApi = {
  // Check if user is admin
  checkUserRole: async (): Promise<{ is_admin: boolean; role: string }> => {
    try {
      // For now, we'll simulate admin detection
      // In a real app, this would be an API call to verify the user's role
      return { is_admin: false, role: 'user' }
    } catch (error) {
      return { is_admin: false, role: 'user' }
    }
  },
}

export default {
  products: productApi,
  cart: cartApi,
  orders: orderApi,
  auth: authApi,
}